import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import DropdownChannel from "components/Common/DropdownChannel";
import DropdownAsyncAgency from "components/Common/DropdownAsyncAgency";
import {
  AGENCY_APP_NAME_OPTION,
  LIST_CHANNEL_OPTION,
  PERIOD_DATE_OPTION,
} from "helpers/constans";
import {
  getCampaignDetail,
  postCampaign,
  putCampaign,
} from "store/agencyCampaign/thunk";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import moment from "moment";
import { formatDateStringToKorea } from "helpers/format";
import CustomParametersForm from "components/Common/CustomParametersForm";
import { IParams } from "api/types/_params";
import { unique } from "helpers/validate_yup";

export interface Props {
  id?: string;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);
const PARAMS_KEY_DEFINE = ['agency', 'campaign'];

const CampaignForm = ({
  id = "",
  triggerRefresh,
  isShowDetail = false,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [selectedAgency, setSelectedAgency] = useState<any>(null);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);
  const [appNameForFilter, setAppNameForFilter] = useState<string>("baive");
  const [typePeriodDate, setTypePeriodDate] = useState<string>("2");

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);
      const filterDataParams = values?.params?.filter(
        (item: IParams) => item?.param_key || item?.param_value
      );
      const formatDataParams =
        filterDataParams?.length > 0
          ? filterDataParams?.map((item: IParams, index: number) => ({
              ordering: item?.ordering,
              param_key: item?.param_key,
              param_value: item?.param_value,
              description: item?.description,
              status: item?.status,
            }))
          : [];

      const data = {
        name: values?.name,
        app_name: values?.app_name,
        // channel: values?.channel?.value,
        params: formatDataParams,
        // status: values?.status === true ? 1 : 2,
        ...(values?.agency_id && { agency_id: values?.agency_id }),
        ...(typePeriodDate != "2" && {
          start_date: moment(values?.start_date)
            .utc()
            .format("YYYY-MM-DD HH:mm:ss"),
          end_date: moment(values?.end_date)
            .utc()
            .format("YYYY-MM-DD HH:mm:ss"),
        }),
        // ...(id && {referral_code: values?.referral_code}),
        // ...(id && {updated_at: values?.updated_at}),
      };

      const response: any = id
        ? await putCampaign(id, data)
        : await postCampaign(data);
      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(`${t("Campaign Name is required")}`),
    app_name: Yup.string().required(`${t("App Name is required")}`),
    params: Yup.array()
      .of(
        Yup.object().shape({
          param_key: Yup.string().matches(
            /^[A-Za-z]+$/,
            `${t("Only allow characters, no spaces")}`
          ).test("param_key", `${t("The key cannot be 'agency' or 'campaign'")}`, (value) => !PARAMS_KEY_DEFINE.includes(value || '')),
          param_value: Yup.string().when("param_key", {
            is: (value: any) => !!value,
            then: (rule) => {
              return rule.test(
                "param_key",
                `${t("This field is required")}`,
                (target) => !!target?.trim()
              );
            },
          }),
        })
      )
      .unique("Key is duplicated", (a: any) => a.param_key),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      app_name: AGENCY_APP_NAME_OPTION[0]?.value,
      agency_id: "",
      params: [
        {
          param_key: "",
          param_value: "",
          description: "",
          status: 1,
          ordering: 1,
          id: 1,
        },
      ] as IParams[],
      referral_code: "",
      // channel: LIST_CHANNEL_OPTION[0]?.value,
      start_date: new Date(),
      end_date: new Date(new Date().setDate(new Date().getDate() + 7)),
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    if (valueDefault?.agency) {
      let vAgencyOption: any = valueDefault?.agency ? (
        {
          value: valueDefault?.agency?.id,
          label: (
            <div className="d-flex align-items-center gap-2">
              <div>{`${
                valueDefault?.agency?.agency_name
                  ? valueDefault?.agency?.agency_name
                  : ""
              }`}</div>
            </div>
          ),
          data: valueDefault?.agency,
        }
      ) : (
        <></>
      );
      formik.setFieldValue("agency_id", vAgencyOption?.id || "");
      setSelectedAgency(vAgencyOption);
    }

    if (valueDefault.start_date && valueDefault.end_date) {
      setTypePeriodDate("1");
    }

    formik.setValues({
      ...valueDefault,
      start_date: valueDefault.start_date
        ? new Date(formatDateStringToKorea(valueDefault.start_date))
        : null,
      end_date: valueDefault.end_date
        ? new Date(formatDateStringToKorea(valueDefault.end_date))
        : null,
      params: valueDefault?.agency_params ?? [],
    });
  };

  const handleCallCampaignDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getCampaignDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    handleCallCampaignDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Campaign")} - ${t("Campaign")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Campaign") : t("Create Campaign")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 2) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  <Col lg={id ? 12 : 6}>
                    <div className="mb-3">
                      <Label htmlFor="campaign-name" className="form-label">
                        {t("Campaign Name")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="campaign-name"
                        name="name"
                        type="text"
                        className="form-control"
                        placeholder={t("Enter your campaign name")}
                        value={formik?.values?.name}
                        onChange={(event: any) => {
                          setIsFirstProgress(false);
                          formik.setFieldValue("name", event?.target?.value);
                        }}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  {id && (
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="referral_code" className="form-label">
                          {t("Referral Code")}
                        </Label>
                        <Input
                          name="referral_code"
                          type="text"
                          className="form-control"
                          id="referral_code"
                          disabled={true}
                          placeholder={t("Enter your referral code")}
                          value={formik?.values?.referral_code}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "referral_code",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.referral_code &&
                        formik.errors.referral_code ? (
                          <div className="text-danger mt-2">
                            {formik.errors.referral_code}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  )}

                  {/* <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Channel")}</Label>

                      <DropdownChannel
                        name="channel"
                        placeholder={`${t("Channel")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.channel || null}
                        onChangeSelect={(channel: any) => {
                          formik.setFieldValue("channel", channel?.value);
                        }}
                        isHasOptionAll={false}
                        isClearable={true}
                      />

                      {formik?.touched?.channel && formik?.errors?.channel ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.channel}
                        </div>
                      ) : null}
                    </div>
                  </Col> */}

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("App")}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownOptionNew
                        name="app_name"
                        dataList={AGENCY_APP_NAME_OPTION || []}
                        placeholder={`${t("Select App Name")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.app_name}
                        // onChangeSelect={(e: any) =>
                        //   formik.setFieldValue("app_name", e?.value)
                        // }
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("app_name", event?.value);
                          setAppNameForFilter(event?.value);
                        }}
                        isHasOptionAll={false}
                        lstImage={true}
                        hideSelectedOptions={false}
                      />
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Agency")}</Label>

                      <DropdownAsyncAgency
                        name="agency_id"
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("agency_id", event?.value);
                          setSelectedAgency(event);
                        }}
                        isMulti={false}
                        placeholder={`${t("Select Agency")}...`}
                        isShowIconCheck={false}
                        isAllOption={false}
                        listCheckedUsers={selectedAgency}
                        keywordSearch={appNameForFilter}
                        key={`list-${appNameForFilter || "all"}`}
                      />

                      {formik?.touched?.agency_id &&
                      formik?.errors?.agency_id ? (
                        <div className="text-danger mt-2">
                          {formik?.errors?.agency_id}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="titleKoreaInput" className="form-label">
                        {t("Campaign Duration")}{" "}
                        <span className="text-danger"></span>
                      </Label>

                      <div className="d-flex justify-content-start align-items-center">
                        {PERIOD_DATE_OPTION?.map((item) => {
                          return (
                            <div
                              className="d-flex justify-content-start align-items-center form-check me-3"
                              onClick={() => {
                                setTypePeriodDate(item?.value);
                              }}
                            >
                              <input
                                className="form-check-input me-2"
                                type="radio"
                                name="gender"
                                id={item?.value}
                                value={item?.value}
                                checked={item?.value === typePeriodDate}
                                style={{ minWidth: "16px" }}
                                onChange={(event) => {
                                  setTypePeriodDate(event?.target?.value);
                                }}
                              />
                              <label
                                className="form-check-label position-relative"
                                htmlFor={item?.value}
                              >
                                {t(item?.label)}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Col>

                  {typePeriodDate === "1" && (
                    <>
                      <Col lg={6} className="datepicker-time">
                        <div className="mb-3">
                          <Label htmlFor="start_date" className="form-label">
                            {t("Start Date")}
                          </Label>

                          <div className="w-100">
                            <DatePicker
                              name={"start_date"}
                              className="form-control"
                              selected={formik?.values?.start_date}
                              placeholderText={t("Select start date")}
                              onChange={(date: any) => {
                                formik.setFieldValue("start_date", date);
                              }}
                              dateFormat="yyyy-MM-dd HH:mm:ss"
                              isClearable={true}
                              showTimeSelect
                            />
                          </div>

                          {formik.touched.start_date &&
                          formik.errors.start_date ? (
                            <div className="text-danger mt-2">
                              {/* {formik.errors.start_date} */}
                            </div>
                          ) : null}
                        </div>
                      </Col>

                      <Col lg={6} className="datepicker-time">
                        <div className="mb-3">
                          <Label htmlFor="end_date" className="form-label">
                            {t("End Date")}
                          </Label>

                          <div className="w-100">
                            <DatePicker
                              name={"end_date"}
                              className="form-control"
                              selected={formik?.values?.end_date}
                              placeholderText={t("Select end date")}
                              onChange={(date: any) => {
                                formik.setFieldValue("end_date", date);
                              }}
                              dateFormat="yyyy-MM-dd HH:mm:ss"
                              isClearable={true}
                              showTimeSelect
                            />
                          </div>

                          {formik.touched.end_date && formik.errors.end_date ? (
                            <div className="text-danger mt-2">
                              {/* {formik.errors.end_date} */}
                            </div>
                          ) : null}
                        </div>
                      </Col>
                    </>
                  )}

                  <CustomParametersForm formik={formik} />
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update Campaign")
                          : t("Button Create Campaign")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default CampaignForm;
